import { useState } from "react";
import {
    Alert,
    Box,
    Button,
    FormControl,
    InputLabel,
    MenuItem,
    Select,
    Stack,
    TextField,
    Typography,
} from "@mui/material";

import IsoDateField from "../../../../components/common/IsoDateField";
import { useClient360SalesActivity } from "./useClient360SalesActivity";

function formatFollowUp(value: string | null | undefined) {
    if (!value) {
        return "—";
    }

    const date = new Date(value);

    return Number.isNaN(date.getTime()) ? value : date.toLocaleString("bg-BG");
}

export default function Client360SalesActivityCard({ profileId }: { profileId: string }) {
    const { query, noteMutation, statusMutation, statusOptions } = useClient360SalesActivity(profileId);
    const [noteBody, setNoteBody] = useState("");
    const [followUpAt, setFollowUpAt] = useState("");

    const notes = query.data?.notes ?? [];
    const salesStatus = query.data?.salesStatus ?? null;
    const saveError =
        (noteMutation.error instanceof Error ? noteMutation.error.message : null)
        ?? (statusMutation.error instanceof Error ? statusMutation.error.message : null);

    async function handleSaveNote() {
        const body = noteBody.trim();

        if (body === "") {
            return;
        }

        await noteMutation.mutateAsync({
            body,
            followUpAt: followUpAt !== "" ? `${followUpAt}T09:00:00` : null,
        });
        setNoteBody("");
        setFollowUpAt("");
    }

    return (
        <Box
            sx={{
                p: 2,
                mb: 2,
                borderRadius: 3,
                border: "1px solid rgba(216, 225, 235, 0.9)",
                backgroundColor: "rgba(255, 255, 255, 0.55)",
            }}
        >
            <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 0.5 }}>
                Търговска активност
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Бележки и статус за работа с клиента (търговец / супервайзор).
            </Typography>

            <FormControl fullWidth size="small" sx={{ mb: 2, maxWidth: { sm: 360 } }}>
                <InputLabel id="client-sales-status" shrink>
                    Търговски статус
                </InputLabel>
                <Select
                    labelId="client-sales-status"
                    label="Търговски статус"
                    value={salesStatus?.statusCode ?? ""}
                    displayEmpty
                    renderValue={(value) => {
                        if (value === "") {
                            return (
                                <Typography component="span" variant="body2" color="text.secondary">
                                    Не е зададен
                                </Typography>
                            );
                        }

                        const option = statusOptions.find((row) => row.code === value);

                        return option?.label ?? value;
                    }}
                    onChange={(event) => {
                        const code = event.target.value;

                        if (code !== "") {
                            statusMutation.mutate(code);
                        }
                    }}
                >
                    <MenuItem value="">
                        <em>Не е зададен</em>
                    </MenuItem>
                    {statusOptions.map((option) => (
                        <MenuItem key={option.code} value={option.code}>
                            {option.label}
                        </MenuItem>
                    ))}
                </Select>
            </FormControl>

            <Stack spacing={1.5} sx={{ mb: 2 }}>
                <TextField
                    label="Нова бележка"
                    value={noteBody}
                    onChange={(event) => setNoteBody(event.target.value)}
                    multiline
                    minRows={2}
                    fullWidth
                />
                <Stack direction={{ xs: "column", sm: "row" }} spacing={1.5} sx={{ alignItems: { sm: "center" } }}>
                    <IsoDateField
                        label="Follow-up дата"
                        value={followUpAt}
                        onChange={setFollowUpAt}
                        fullWidth
                    />
                    <Button
                        variant="contained"
                        onClick={handleSaveNote}
                        disabled={noteMutation.isPending || noteBody.trim() === ""}
                    >
                        Запази бележка
                    </Button>
                </Stack>
            </Stack>

            {saveError ? <Alert severity="error" sx={{ mb: 2 }}>{saveError}</Alert> : null}

            {query.isPending ? (
                <Typography variant="body2" color="text.secondary">
                    Зареждане на бележки…
                </Typography>
            ) : notes.length === 0 ? (
                <Typography variant="body2" color="text.secondary">
                    Няма записани бележки за този клиент.
                </Typography>
            ) : (
                <Stack spacing={1.25}>
                    {notes.map((note) => (
                        <Box
                            key={note.id}
                            sx={{
                                p: 1.25,
                                borderRadius: 2,
                                border: "1px solid rgba(216, 225, 235, 0.75)",
                            }}
                        >
                            <Typography variant="body2" sx={{ whiteSpace: "pre-wrap" }}>
                                {note.body}
                            </Typography>
                            <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 0.75 }}>
                                {new Date(note.createdAt).toLocaleString("bg-BG")}
                                {" · Follow-up: "}
                                {formatFollowUp(note.followUpAt)}
                            </Typography>
                        </Box>
                    ))}
                </Stack>
            )}
        </Box>
    );
}
